import { useCallback, useRef, type KeyboardEvent } from "react";
import { ArrowRight, FilePlus2, Upload } from "lucide-react";
import { cn } from "utils/cn";
import type { ProjectCreateSource } from "../../types/project-create-source.types";
import type { SourcePickerProps } from "./SourcePicker.types";

const SOURCES: {
  id: ProjectCreateSource;
  title: string;
  description: string;
  icon: typeof FilePlus2;
}[] = [
  {
    id: "blank",
    title: "Start blank",
    description: "Begin with an empty screenplay and add scenes as you go.",
    icon: FilePlus2,
  },
  {
    id: "import",
    title: "Import screenplay",
    description: "Upload a PDF, Fountain, FDX, or Celtx file to parse into the editor.",
    icon: Upload,
  },
];

export const SourcePicker = ({ onSelect }: SourcePickerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const focusTile = useCallback((index: number) => {
    const el = containerRef.current?.querySelector<HTMLButtonElement>(
      `[data-tile-index="${index}"]`
    );
    el?.focus();
  }, []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") {
        return;
      }
      event.preventDefault();
      const buttons =
        containerRef.current?.querySelectorAll<HTMLButtonElement>("[data-tile-index]");
      if (!buttons?.length) return;
      const active = document.activeElement;
      let idx = 0;
      buttons.forEach((btn, i) => {
        if (btn === active) idx = i;
      });
      const next =
        event.key === "ArrowRight" ? Math.min(idx + 1, buttons.length - 1) : Math.max(idx - 1, 0);
      focusTile(next);
    },
    [focusTile]
  );

  return (
    <div
      ref={containerRef}
      role="listbox"
      aria-label="Choose how to start your project"
      className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      onKeyDown={onKeyDown}
    >
      {SOURCES.map((item, index) => {
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            type="button"
            data-tile-index={index}
            role="option"
            aria-selected={false}
            className={cn(
              "group relative flex h-full flex-col gap-5 rounded-xl border border-border bg-card p-5 text-left",
              "transition-all duration-150 ease-out",
              "hover:-translate-y-0.5 hover:border-primary/50 hover:bg-muted/50 hover:shadow-[0_8px_24px_-12px_rgb(var(--primary)/0.25)]",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            )}
            onClick={() => onSelect(item.id)}
          >
            <div className="flex items-start justify-between">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/20">
                <Icon className="h-6 w-6" aria-hidden />
              </div>
              <ArrowRight
                className="h-5 w-5 -translate-x-1 text-muted-foreground/50 opacity-0 transition-all duration-150 group-hover:translate-x-0 group-hover:text-primary group-hover:opacity-100"
                aria-hidden
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-base font-semibold text-foreground">{item.title}</span>
              <span className="text-sm leading-relaxed text-muted-foreground">
                {item.description}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};
